import { COUNTRIES } from '../countries/countriesData';
import { FIELD_BINDING_CATALOGUE } from './fieldBindingCatalogue';
import { splitFilters } from '../common/business/documentTemplate';

/**
 * Where a data point's allowed values come from.
 *
 * Named rather than listed on each entry, for two reasons. The catalogue stays readable, and —
 * the load-bearing one — the options can never drift from the list the value was actually
 * chosen from: both resolve from `COUNTRIES` here.
 *
 * Country is TWO sources on purpose. The address forms store a country's NAME
 * (`COUNTRIES_GROUPED_OPTIONS`) while the tax-residence and country-of-birth forms store its
 * CODE (`COUNTRIES_NAME_CODE_GROUPED_OPTIONS`), so one shared "country" list would silently
 * never match on half the data points a person would reach for.
 */
export const DATA_POINT_CHOICE_SOURCES = ['countryName', 'countryCode'] as const;
export type DataPointChoiceSource = (typeof DATA_POINT_CHOICE_SOURCES)[number];

/**
 * Narrows a source read off the wire. The generated API model types it as a plain `string`, so
 * the frontend takes it as one and narrows here rather than trusting the shape it was told.
 */
export const toDataPointChoiceSource = (
	value: string | undefined
): DataPointChoiceSource | undefined =>
	DATA_POINT_CHOICE_SOURCES.find((source) => source === value);

/**
 * The value list a data point declares, by catalogue path.
 *
 * Takes the path APART first: a bound field may carry Liquid filters (`… | date: "%d %b %Y"`)
 * and the catalogue is keyed on the bare expression, so an exact match on the raw path would
 * find nothing and quietly report the point as free text.
 */
export const choiceSourceForPath = (path: string): DataPointChoiceSource | undefined =>
	FIELD_BINDING_CATALOGUE.find((point) => point.path === splitFilters(path).expression)?.choices;

export interface DataPointChoice {
	/** Compared against the stored value, so this is the form the database holds. */
	value: string;
	/** What a person picks from — always the readable country name. */
	label: string;
}

/** The values a data point can hold, as the picker offers them. */
export const dataPointChoices = (source: DataPointChoiceSource): DataPointChoice[] =>
	COUNTRIES.map((country) => ({
		value: source === 'countryCode' ? country.country_code : country.name,
		label: country.name,
	}));

/**
 * Compares a stored value to a chosen one.
 *
 * Case- and whitespace-insensitive because the codebase already assumes imported data is not
 * clean — `countryNameFor` upper-cases before looking a code up, with the comment that codes
 * "should be stored uppercase but imported data isn't guaranteed to be". A case-sensitive
 * comparison here would turn that known dirtiness into a missing clause.
 */
export const sameChoiceValue = (left: string, right: string): boolean =>
	left.trim().toLocaleLowerCase() === right.trim().toLocaleLowerCase();
